import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import type { GeometrieMacro, TipProiect } from "../api";
import IlustratieProiect from "../components/IlustratieProiect";
import { Ic } from "../components/ui";
import { useSession } from "../session";
import { useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { PASI, PasiDeCitit } from "./Pasi";
import { PAL, alfa } from "../structure/paletaDesen";

// PROIECTUL NOU, în doi sau trei pași: ce fel de proiect, la structură cum
// pornește ea, apoi numele.
//
// Tipul vine întâi fiindcă el hotărăște tot ce urmează: ce cere dosarul, ce
// pași are drumul, ce se generează. Un proiect COMPLET pornește din actele
// terenului și primește clădirea întreagă; unul de STRUCTURĂ pornește dintr-o
// arhitectură gata făcută și primește structura și devizul ei. Cele două
// carduri stau alături și desenează aceeași clădire — întreagă, respectiv doar
// scheletul ei —, ca diferența să se vadă înainte să se citească.
//
// Proiectul de structură mai are o alegere, după tip: de unde vine GEOMETRIA
// MACRO — pozițiile și dimensiunile elementelor verticale și pozițiile
// grinzilor. Optimus o propune pe arhitectura importată, sau o citește din
// planșele care o au deja desenată (analiza unei structuri existente).
// Calculul de detaliu e același pe amândouă. Alegerea hotărăște ce pornește
// SINGUR după importul planșelor (`core.project.geometrie_macro`); din lista
// de soluții se pornește oricând și cealaltă, pe același proiect.
//
// Rândul proiectului se scrie abia la «Creează proiectul»: o alegere lăsată la
// jumătate nu lasă în listă un proiect gol, fără nume.
//
// Fiecare pas are ruta lui (`/projects/new`, `/projects/new/<tip>`,
// `/projects/new/structura/<geometrie>`), deci «Înapoi» din browser întoarce
// la alegerea de dinainte, iar un link spre nume poate duce direct acolo.
// Organizația aleasă în listă vine cu `?org=`.

export const TIPURI: TipProiect[] = ["complet", "structura"];
export const GEOMETRII: GeometrieMacro[] = ["generata", "importata"];

type Texte = { deLa: Cheie; desc: Cheie; incarci: Cheie; cta: Cheie; desen: Cheie };

const TEXTE: Record<TipProiect, Texte> = {
  complet: {
    deLa: "proiect_nou.complet_de_la",
    desc: "proiect_nou.complet_desc",
    incarci: "proiect_nou.complet_incarci",
    cta: "proiect_nou.complet_cta",
    desen: "proiect_nou.complet_desen",
  },
  structura: {
    deLa: "proiect_nou.structura_de_la",
    desc: "proiect_nou.structura_desc",
    incarci: "proiect_nou.structura_incarci",
    cta: "proiect_nou.structura_cta",
    desen: "proiect_nou.structura_desen",
  },
};

type TexteGeometrie = { eticheta: Cheie; nume: Cheie; desc: Cheie; incarci: Cheie; cta: Cheie;
                        scurt: Cheie };

const TEXTE_GEOMETRIE: Record<GeometrieMacro, TexteGeometrie> = {
  generata: {
    eticheta: "proiect_nou.generata_eticheta",
    nume: "proiect_nou.generata_nume",
    desc: "proiect_nou.generata_desc",
    incarci: "proiect_nou.generata_incarci",
    cta: "proiect_nou.generata_cta",
    scurt: "proiect_nou.geometrie_generata_scurt",
  },
  importata: {
    eticheta: "proiect_nou.importata_eticheta",
    nume: "proiect_nou.importata_nume",
    desc: "proiect_nou.importata_desc",
    incarci: "proiect_nou.importata_incarci",
    cta: "proiect_nou.importata_cta",
    scurt: "proiect_nou.geometrie_importata_scurt",
  },
};

/** Exemplele de verificări din normativ pe care le trece calculul de detaliu. */
const VERIFICARI: Cheie[] = [
  "proiect_nou.verif_1", "proiect_nou.verif_2", "proiect_nou.verif_3",
  "proiect_nou.verif_4", "proiect_nou.verif_5", "proiect_nou.verif_6",
];

/** Ce primește proiectul complet: toate disciplinele, apoi devizul. */
const LIVRABILE_COMPLET: { ic: string; titlu: Cheie }[] = [
  { ic: "situatie", titlu: "livrabil.situatie" },
  { ic: "fatada", titlu: "livrabil.fatada" },
  { ic: "apartamente", titlu: "livrabil.apartamente" },
  { ic: "subsol", titlu: "livrabil.subsol" },
  { ic: "structura", titlu: "livrabil.structura" },
  { ic: "sanitare", titlu: "livrabil.sanitare" },
  { ic: "electrice", titlu: "livrabil.electrice" },
  { ic: "hvac", titlu: "livrabil.hvac" },
  { ic: "deviz", titlu: "livrabil.deviz" },
];

/** Ce primește proiectul de structură: puțin, deci spus pe larg. */
const LIVRABILE_STRUCTURA: { ic: string; titlu: Cheie; desc: Cheie }[] = [
  { ic: "structura", titlu: "livrabil.plan_structura", desc: "livrabil.plan_structura_desc" },
  { ic: "deviz", titlu: "livrabil.deviz_structura", desc: "livrabil.deviz_structura_desc" },
];

export const numarLivrabile = (tip: TipProiect) =>
  tip === "complet" ? LIVRABILE_COMPLET.length : LIVRABILE_STRUCTURA.length;

const eGeometrie = (g: string | undefined): g is GeometrieMacro =>
  g === "generata" || g === "importata";

export default function ProiectNou() {
  const { tip, geometrie } = useParams();
  if (!tip) return <AlegereaTipului />;
  if (tip !== "complet" && tip !== "structura") return <Navigate to="/projects/new" replace />;
  if (tip === "complet") {
    // proiectul complet n-are a doua alegere
    if (geometrie) return <Navigate to="/projects/new/complet" replace />;
    return <NumeleProiectului tip="complet" />;
  }
  if (!geometrie) return <AlegereaGeometriei />;
  if (!eGeometrie(geometrie)) return <Navigate to="/projects/new/structura" replace />;
  return <NumeleProiectului tip="structura" geometrie={geometrie} />;
}

function Cap({ inapoi, la }: { inapoi: string; la: string }) {
  return (
    <div className="pn-cap">
      <Link className="pn-inapoi" to={la}><Ic n="back" s={12} />{inapoi}</Link>
    </div>
  );
}

function AlegereaTipului() {
  const t = useT();
  const { search } = useLocation();
  return (
    <div className="page proiect-nou">
      <Cap inapoi={t("proiect_nou.inapoi_proiecte")} la="/projects" />
      <div className="pn-titlu">
        <span className="lbl pn-acc">{t("proiect_nou.eticheta")}</span>
        <h1 className="disp">{t("proiect_nou.titlu")}</h1>
        <p>{t("proiect_nou.sub")}</p>
      </div>
      <div className="pn-carduri">
        {TIPURI.map((tip) => <CardTip key={tip} tip={tip} search={search} />)}
      </div>
    </div>
  );
}

/** Un tip de proiect ca o ușă: desenul, de unde pornești, ce încarci, ce
 *  primești și câți pași are drumul. TOT cardul e ținta clicului; butonul din
 *  colț e desenat, nu separat, fiindcă un buton într-un link ar fi un al
 *  doilea clic pentru același drum. */
function CardTip({ tip, search }: { tip: TipProiect; search: string }) {
  const t = useT();
  const tn = useTn();
  const x = TEXTE[tip];
  return (
    <Link className="card pn-card" to={`/projects/new/${tip}${search}`}
          aria-label={`${t(`tip.${tip}`)} — ${t(x.deLa)}`}>
      <div className="pn-desen">
        <IlustratieProiect tip={tip} w={540} h={250} pad={[22, 18]} titlu={t(x.desen)} />
      </div>
      <div className="pn-corp">
        <span className="lbl pn-acc">{t(x.deLa)}</span>
        <span className="pn-nume disp">{t(`tip.${tip}`)}</span>
        <span className="pn-desc">{t(x.desc)}</span>
        <div className="pn-sect">
          <span className="lbl">{t("proiect_nou.incarci")}</span>
          <span className="pn-incarci">{t(x.incarci)}</span>
        </div>
        <div className="pn-sect">
          <span className="lbl">{t("proiect_nou.primesti")}</span>
          <Livrabile tip={tip} />
        </div>
        <div className="pn-jos">
          <span className="btn pn-cta">{t(x.cta)}<Ic n="arrow" s={12} /></span>
          <span className="sp" />
          <span className="lbl">{tn("proiect_nou.pasi#n", PASI[tip].length)}</span>
        </div>
      </div>
    </Link>
  );
}

/** A doua alegere a proiectului de structură: de unde vine geometria macro.
 *  Întâi cele două noțiuni — ce e geometria macro, ce e calculul de detaliu și
 *  ce verificări trece —, apoi cele două uși. */
function AlegereaGeometriei() {
  const t = useT();
  const { search } = useLocation();
  return (
    <div className="page proiect-nou">
      <Cap inapoi={t("proiect_nou.inapoi_alegere")} la={`/projects/new${search}`} />
      <div className="pn-titlu">
        <span className="lbl pn-acc">{t("tip.structura")}</span>
        <h1 className="disp">{t("proiect_nou.geometrie_titlu")}</h1>
        <p>{t("proiect_nou.geometrie_sub")}</p>
      </div>
      <div className="pn-definitii">
        <div className="card pn-definitie">
          <span className="pn-icoana mare"
                style={{ color: PAL.stalp, background: alfa(PAL.stalp, 0.1) }}>
            <Ic n="structura" s={14} />
          </span>
          <b>{t("proiect_nou.geometrie_macro_ce")}</b>
          <span>{t("proiect_nou.geometrie_macro_desc")}</span>
        </div>
        <div className="card pn-definitie">
          <span className="pn-icoana mare"
                style={{ color: PAL.stalp, background: alfa(PAL.stalp, 0.1) }}>
            <Ic n="check" s={14} />
          </span>
          <b>{t("proiect_nou.detaliu_ce")}</b>
          <span>{t("proiect_nou.detaliu_desc")}</span>
          <ul className="pn-verificari">
            {VERIFICARI.map((k) => <li key={k}>{t(k)}</li>)}
          </ul>
        </div>
      </div>
      <div className="pn-carduri">
        {GEOMETRII.map((g) => <CardGeometrie key={g} geometrie={g} search={search} />)}
      </div>
      <p className="note pn-oricand">{t("proiect_nou.oricand")}</p>
    </div>
  );
}

function CardGeometrie({ geometrie, search }: { geometrie: GeometrieMacro; search: string }) {
  const t = useT();
  const x = TEXTE_GEOMETRIE[geometrie];
  return (
    <Link className="card pn-card pn-card-geometrie"
          to={`/projects/new/structura/${geometrie}${search}`}
          aria-label={`${t(x.nume)} — ${t(x.eticheta)}`}>
      <div className="pn-corp">
        <span className="lbl pn-acc">{t(x.eticheta)}</span>
        <span className="pn-nume disp">{t(x.nume)}</span>
        <span className="pn-desc">{t(x.desc)}</span>
        <div className="pn-sect">
          <span className="lbl">{t("proiect_nou.incarci")}</span>
          <span className="pn-incarci">{t(x.incarci)}</span>
        </div>
        <div className="pn-sect">
          <span className="lbl">{t("proiect_nou.primesti")}</span>
          <Livrabile tip="structura" />
        </div>
        <div className="pn-jos">
          <span className="btn pn-cta">{t(x.cta)}<Ic n="arrow" s={12} /></span>
        </div>
      </div>
    </Link>
  );
}

function Livrabile({ tip }: { tip: TipProiect }) {
  const t = useT();
  if (tip === "structura") {
    return (
      <ul className="pn-livrabile-mari">
        {LIVRABILE_STRUCTURA.map((l) => (
          <li key={l.titlu}>
            <span className="pn-icoana mare"
                  style={{ color: PAL.stalp, background: alfa(PAL.stalp, 0.1) }}>
              <Ic n={l.ic} s={14} />
            </span>
            <span><b>{t(l.titlu)}</b><span>{t(l.desc)}</span></span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="pn-livrabile">
      {LIVRABILE_COMPLET.map((l) => (
        <li key={l.titlu}>
          <span className="pn-icoana"><Ic n={l.ic} s={12} /></span>{t(l.titlu)}
        </li>
      ))}
    </ul>
  );
}

function NumeleProiectului({ tip, geometrie }: { tip: TipProiect; geometrie?: GeometrieMacro }) {
  const t = useT();
  const tn = useTn();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user } = useSession();
  const organizatii = useMemo(() => user?.organizations ?? [], [user]);
  const dinLista = new URLSearchParams(search).get("org");
  const [org, setOrg] = useState(() =>
    organizatii.find((o) => o.id === dinLista)?.id ?? organizatii[0]?.id ?? "");
  const [nume, setNume] = useState("");
  const [lucrez, setLucrez] = useState(false);
  const [eroare, setEroare] = useState("");
  // «Schimbă» și «Înapoi» duc la alegerea DE DINAINTE: geometria la structură,
  // tipul la proiectul complet
  const inapoi = geometrie ? `/projects/new/structura${search}` : `/projects/new${search}`;

  async function creeaza(e: FormEvent) {
    e.preventDefault();
    const n = nume.trim();
    const organizatie = org || organizatii[0]?.id;
    if (!n || !organizatie || lucrez) return;
    setLucrez(true);
    setEroare("");
    try {
      const proiect = await api.createProject(organizatie, n, tip, geometrie);
      // Proiectul nou n-are încă nimic de arătat: se deschide în dosar, unde
      // chiar urmează să urci actele.
      navigate(`/p/${proiect.id}/dossier`);
    } catch {
      setEroare(t("proiecte.esec_creare"));
      setLucrez(false);
    }
  }

  return (
    <div className="page proiect-nou">
      <Cap inapoi={t("proiect_nou.inapoi_alegere")} la={inapoi} />
      <div className="pn-mijloc">
        <form className="card pn-formular" onSubmit={creeaza}>
          <div className="pn-recap">
            <span className="pn-recap-desen">
              <IlustratieProiect tip={tip} w={82} h={56} pad={[6, 5]} detaliu={false} />
            </span>
            <span className="pn-recap-text">
              <span className="lbl">{t("proiect_nou.ai_ales")}</span>
              <b>{t(`tip.${tip}`)}</b>
              <span>
                {[t(TEXTE[tip].deLa),
                  geometrie ? t(TEXTE_GEOMETRIE[geometrie].scurt) : null,
                  tn("proiect_nou.livrabile#n", numarLivrabile(tip))]
                  .filter(Boolean).join(" · ")}
              </span>
            </span>
            <span className="sp" />
            <Link to={inapoi}>{t("proiect_nou.schimba")}</Link>
          </div>
          <div className="pn-formular-corp">
            <h1 className="disp">{t("proiect_nou.nume_titlu")}</h1>
            {organizatii.length === 0 ? (
              <p className="note bad">{t("proiect_nou.fara_organizatie")}</p>
            ) : (
              <>
                <label className="pn-camp">
                  <span className="lbl">{t("proiect_nou.nume_camp")}</span>
                  <input className="inp" value={nume} autoFocus disabled={lucrez}
                         placeholder={t("proiect_nou.nume_exemplu")}
                         onChange={(e) => setNume(e.target.value)} />
                </label>
                <span className="note pn-nota">{t("proiect_nou.nume_nota")}</span>
                {/* Cu o singură organizație n-are ce alege nimeni: câmpul ar fi
                    o întrebare cu un singur răspuns. */}
                {organizatii.length > 1 && (
                  <label className="pn-camp">
                    <span className="lbl">{t("proiect_nou.organizatia")}</span>
                    <select value={org} disabled={lucrez}
                            onChange={(e) => setOrg(e.target.value)}>
                      {organizatii.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </label>
                )}
                {eroare && <p className="note bad">{eroare}</p>}
                <div className="pn-butoane">
                  <span className="sp" />
                  <Link className="btn btng" to="/projects">{t("comun.renunta")}</Link>
                  <button className="btn btnp" type="submit" disabled={!nume.trim() || lucrez}>
                    {t(lucrez ? "proiect_nou.se_creeaza" : "proiect_nou.creeaza")}
                    <Ic n="arrow" s={12} />
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
        <div className="pn-urmeaza">
          <span className="lbl">{t("proiect_nou.urmeaza")}</span>
          <PasiDeCitit tip={tip} />
        </div>
      </div>
    </div>
  );
}
